/**
 * Notification Service
 * Provides API integration for real-time notification operations
 */

import apiClient from './apiClient';

const NOTIFICATIONS_BASE_URL = '/notifications';

/**
 * Notification API Methods
 */
const notificationService = {
  /**
   * Get paginated notifications for the current user
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {boolean} [params.unread_only=false] - Only return unread notifications
   * @param {string} [params.role] - Filter by recipient role (patient, doctor, clinician, admin)
   * @returns {Promise<Object>} - Paginated notifications with unread count
   */
  getNotifications: async (params = {}) => {
    const { page = 1, limit = 20, unread_only = false, role } = params;
    const queryParams = { page, limit, unread_only };
    if (role) {
      queryParams.role = role;
    }
    // Base route needs trailing slash (defined as @router.get("/"))
    const response = await apiClient.get(`${NOTIFICATIONS_BASE_URL}/`, {
      params: queryParams
    });
    return response.data;
  },

  /**
   * Get the count of unread notifications
   * @param {string} [role] - Filter by recipient role (patient, doctor, clinician, admin)
   * @returns {Promise<number>} - Unread notification count
   */
  getUnreadCount: async (role) => {
    const params = role ? { role } : {};
    const response = await apiClient.get(`${NOTIFICATIONS_BASE_URL}/unread-count`, { params });
    return response.data.unread_count;
  },

  /**
   * Get a specific notification by ID
   * @param {string} notificationId - Notification UUID
   * @returns {Promise<Object>} - Notification details
   */
  getNotification: async (notificationId) => {
    const response = await apiClient.get(`${NOTIFICATIONS_BASE_URL}/${notificationId}`);
    return response.data;
  },

  /**
   * Mark a specific notification as read
   * @param {string} notificationId - Notification UUID
   * @returns {Promise<Object>} - Updated notification
   */
  markAsRead: async (notificationId) => {
    const response = await apiClient.patch(`${NOTIFICATIONS_BASE_URL}/${notificationId}/read`);
    return response.data;
  },

  /**
   * Mark all notifications as read
   * @param {string} [role] - Filter by recipient role (patient, doctor, clinician, admin)
   * @returns {Promise<Object>} - Result with count of marked notifications
   */
  markAllAsRead: async (role) => {
    const params = role ? { role } : {};
    const response = await apiClient.patch(`${NOTIFICATIONS_BASE_URL}/mark-all-read`, null, { params });
    return response.data;
  },

  /**
   * Delete a specific notification
   * @param {string} notificationId - Notification UUID
   * @returns {Promise<Object>} - Deletion result
   */
  deleteNotification: async (notificationId) => {
    const response = await apiClient.delete(`${NOTIFICATIONS_BASE_URL}/${notificationId}`);
    return response.data;
  }
};

/**
 * Notification type display helpers
 */
export const notificationTypeConfig = {
  booking_created: {
    label: 'New Appointment',
    icon: 'calendar-plus',
    color: 'green',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800'
  },
  booking_cancelled: {
    label: 'Appointment Cancelled',
    icon: 'calendar-x',
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800'
  },
  booking_completed: {
    label: 'Appointment Completed',
    icon: 'calendar-check',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800'
  },
  booking_reminder: {
    label: 'Appointment Reminder',
    icon: 'bell',
    color: 'yellow',
    bgColor: 'bg-yellow-100',
    textColor: 'text-yellow-800'
  },
  vitals_uploaded: {
    label: 'Vital Signs Received',
    icon: 'heart-pulse',
    color: 'purple',
    bgColor: 'bg-purple-100',
    textColor: 'text-purple-800'
  },
  ecg_result_uploaded: {
    label: 'ECG Result Available',
    icon: 'activity',
    color: 'indigo',
    bgColor: 'bg-indigo-100',
    textColor: 'text-indigo-800'
  },
  spirometry_uploaded: {
    label: 'Spirometry Result Available',
    icon: 'wind',
    color: 'cyan',
    bgColor: 'bg-cyan-100',
    textColor: 'text-cyan-800'
  },
  notes_updated: {
    label: 'Notes Updated',
    icon: 'file-text',
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800'
  },
  daily_bookings_report: {
    label: 'Daily Report',
    icon: 'bar-chart',
    color: 'orange',
    bgColor: 'bg-orange-100',
    textColor: 'text-orange-800'
  },
  system_alert: {
    label: 'System Alert',
    icon: 'alert-triangle',
    color: 'red',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800'
  }
};

/**
 * Get notification type configuration
 * @param {string} notificationType - Notification type string
 * @returns {Object} - Type configuration with label, icon, colors
 */
export const getNotificationTypeConfig = (notificationType) => {
  return notificationTypeConfig[notificationType] || {
    label: 'Notification',
    icon: 'bell',
    color: 'gray',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800'
  };
};

/**
 * Format notification timestamp for display
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} - Formatted relative time string
 */
export const formatNotificationTime = (timestamp) => {
  if (!timestamp) return '';
  
  const now = new Date();
  const notifDate = new Date(timestamp);
  const diffMs = now - notifDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return notifDate.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: notifDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

export default notificationService;
